import { MessageCircle, Pencil, ArrowRight, X, AlertTriangle, Clock3, BellOff } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format, differenceInCalendarDays, parseISO, isValid } from "date-fns";
import { memo } from "react";
import { ETAPAS, ETAPA_LABEL } from "@/lib/stages";

const todayStr = () => {
  try { return format(new Date(), "yyyy-MM-dd"); } catch { return ""; }
};

function safeParse(s) {
  if (!s || typeof s !== "string") return null;
  try {
    const d = parseISO(s);
    if (!isValid(d)) return null;
    const y = d.getFullYear();
    if (y < 1970 || y > 3000) return null;
    return d;
  } catch { return null; }
}

function safeFormat(s, fmt) {
  const d = safeParse(s);
  if (!d) return "";
  try { return format(d, fmt); } catch { return ""; }
}

function retornoClass(retornarEm) {
  if (!retornarEm) return null;
  const d = safeParse(retornarEm);
  if (!d) return null;
  const t = todayStr();
  if (retornarEm < t) return "overdue";
  if (retornarEm === t) return "today";
  return "future";
}

function paradoDias(c, limitDias) {
  if (!c.ultima_atividade || !["m1", "m2", "m3", "m4"].includes(c.etapa)) return 0;
  const d = safeParse(c.ultima_atividade);
  if (!d) return 0;
  try {
    const dias = differenceInCalendarDays(new Date(), d);
    return dias >= limitDias ? dias : 0;
  } catch { return 0; }
}

export const ContactCard = memo(function ContactCard({ contact, etapa, onEdit, onWhatsApp, onNote, onMove, onSchedule, onDelete, cardParadoDias = 3, isActive = false }) {
  const c = contact;
  const retCls = retornoClass(c.retornar_em);
  const dias = paradoDias(c, cardParadoDias);
  const estaOptOut = !!c.opt_out;

  let border = "";
  if (etapa === "agendado") border = "border-l-[3px] border-l-green-500";
  else if (etapa === "perdido") border = "border-l-[3px] border-l-red-400/80";
  else if (retCls === "overdue") border = "border-l-[3px] border-l-red-500";
  else if (retCls === "today") border = "border-l-[3px] border-l-amber-500";
  else if (dias > 0) border = "border-l-[3px] border-l-amber-400/70";

  let dateStr = "";
  if (c.created_at) dateStr = safeFormat(c.created_at, "dd/MM/yy");

  const waDisabled = estaOptOut || c.whatsapp_valid === false;

  const activeShadow = "0 10px 24px rgba(27, 67, 50, 0.15), 0 2px 6px rgba(27, 67, 50, 0.08)";
  const normalShadow = "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)";

  return (
    <div
      data-testid={`kanban-card-${c.id}`}
      data-active={isActive ? "1" : undefined}
      onClick={onEdit}
      tabIndex={0}
      style={{
        boxShadow: isActive ? activeShadow : normalShadow,
        backgroundColor: isActive ? "rgba(27, 67, 50, 0.05)" : undefined,
        outline: isActive ? "2px solid #2d6a4f" : undefined,
        outlineOffset: isActive ? "-2px" : undefined,
      }}
      className={`bg-[#FAFBFC] rounded-xl px-5 py-4 cursor-pointer transition-all duration-200 ${isActive ? "" : "hover:shadow-md hover:-translate-y-0.5 hover:border-[#d4d4d8]"} focus:outline focus:outline-2 focus:outline-[#2d6a4f] focus:outline-offset-[-2px] ${border}`}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-[13px] font-semibold text-[#1A1A1A] leading-snug">{c.nome}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${c.tipo === "novo" ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-[#888]"}`}>
          {c.tipo === "novo" ? "N" : "B"}
        </span>
      </div>

      <p className="text-xs text-[#999] mb-2">{c.telefone}</p>

      {estaOptOut && (
        <p className="text-[10px] text-red-600 bg-red-50 rounded px-2 py-0.5 mb-2 inline-flex items-center gap-1" data-testid="card-opt-out">
          <BellOff className="h-3 w-3" /> Não quer mais contato
        </p>
      )}

      <div className="flex items-center justify-between mb-2">
        {c.responsavel && <span className="text-[11px] text-[#888]">{c.responsavel}</span>}
        {dateStr && <span className="text-[10px] text-[#BBB]" data-testid="card-date">{dateStr}</span>}
      </div>

      {c.retornar_em && safeParse(c.retornar_em) && (
        <p className={`text-[11px] mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded ${retCls === "overdue" ? "bg-red-50 text-red-700" : retCls === "today" ? "bg-amber-50 text-amber-700" : "bg-zinc-100 text-zinc-600"}`} data-testid="card-retornar-em">
          <Clock3 className="h-3 w-3" /> Retornar em {safeFormat(c.retornar_em, "dd/MM")}
          {c.proxima_acao ? ` · ${c.proxima_acao.slice(0, 40)}` : ""}
        </p>
      )}

      {dias > 0 && (
        <p className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-0.5 mb-2 inline-flex items-center gap-1" data-testid="card-parado">
          <AlertTriangle className="h-3 w-3" /> Parado há {dias} dia{dias > 1 ? "s" : ""}
        </p>
      )}

      {c.agendado_data && (
        <p className="text-xs text-green-600 mb-2">
          {c.agendado_data}{c.agendado_valor > 0 && ` · R$${c.agendado_valor}`}
        </p>
      )}

      {c.anotacao && c.anotacao.length <= 200 && (
        <p className="text-[11px] text-amber-700/90 bg-amber-50 rounded-md px-2.5 py-1 mb-3 truncate" data-testid="card-note-preview">
          {c.anotacao}
        </p>
      )}

      <div className="flex items-center gap-2 pt-3 border-t border-[#F0F0F0]" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="action-whatsapp"
          onClick={onWhatsApp}
          disabled={waDisabled}
          title={waDisabled ? (estaOptOut ? "Contato marcou não quer mais contato" : "Telefone inválido") : "WhatsApp"}
          className={`text-[11px] px-2.5 py-1 rounded-md transition-all duration-200 ${waDisabled ? "text-gray-400 cursor-not-allowed" : "text-green-600 hover:bg-green-50"}`}
        >
          <MessageCircle className="h-3 w-3 inline mr-1" />
          WhatsApp
        </button>

        <button
          data-testid="action-note"
          onClick={onNote}
          className={`text-[11px] px-2 py-1 rounded border transition-all duration-200 relative ${c.anotacao ? "text-amber-600 border-amber-300 hover:bg-amber-50" : "text-[#999] border-transparent hover:bg-gray-100 hover:text-[#555]"}`}
        >
          <Pencil className="h-3 w-3 inline mr-1" />
          Nota
          {c.anotacao && (<span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-400 rounded-full" />)}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-[11px] px-2.5 py-1 rounded-md text-[#999] hover:bg-gray-100 hover:text-[#555] transition-all duration-200" data-testid="action-mover">
              <ArrowRight className="h-3 w-3 inline mr-1" />
              Mover
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-zinc-900 border-zinc-700 min-w-[140px]">
            {ETAPAS.filter((e) => e.id !== etapa).map((e) => (
              <DropdownMenuItem
                key={e.id}
                onClick={() => onMove(e.id)}
                className="text-xs text-zinc-300 focus:bg-zinc-800 focus:text-zinc-100"
                data-testid={`mover-${e.id}`}
              >
                {e.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <button onClick={onSchedule} className="text-[11px] px-2.5 py-1 rounded-md text-[#999] hover:bg-gray-100 hover:text-[#555] transition-all duration-200">
          Agendar
        </button>

        <button data-testid="action-delete" onClick={onDelete} className="text-[#CCC] hover:text-red-400 transition-all duration-200 ml-auto p-1">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
});

// Reexporta ETAPA_LABEL para quem precisar
export { ETAPA_LABEL };
